import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api";
import { IconTrash } from "@/components/admin/Icons";

type FeedbackRow = {
  id: number;
  nome: string;
  email: string;
  telefone: string;
  rating: number;
  sentiment: "ruim" | "neutra" | "boa";
  comentario: string | null;
  created_at: string;
};

type MetricRow = {
  date: string;
  total: number;
  boa: number;
  neutra: number;
  ruim: number;
  avg_rating: number;
};

type ResponseBody = {
  feedback: FeedbackRow[];
  metrics: MetricRow[];
  total: number;
  page: number;
  pageSize: number;
};

const SENTIMENT_LABEL = {
  boa: "Boa",
  neutra: "Neutra",
  ruim: "Ruim",
} satisfies Record<FeedbackRow["sentiment"], string>;

function fmtDateTime(value: string): string {
  const date = new Date(`${value.replace(" ", "T")}Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtMetricDay(value: string): string {
  const day = value.split("-")[2];
  return day ? `Dia ${Number(day)}` : value;
}

function dayTone(metric: MetricRow): "boa" | "neutra" | "ruim" {
  if (metric.boa >= metric.ruim && metric.boa >= metric.neutra) return "boa";
  if (metric.ruim > metric.boa && metric.ruim >= metric.neutra) return "ruim";
  return "neutra";
}

export default function AdminPalestras() {
  const [data, setData] = useState<ResponseBody | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadFeedback = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", "25");
    if (search) params.set("search", search);

    apiFetch<ResponseBody>(`/admin/lecture-feedback?${params.toString()}`, { auth: "admin" })
      .then(setData)
      .catch(() => setError("Falha ao carregar avaliacoes da palestra."))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const handleDelete = async (row: FeedbackRow) => {
    const confirmed = window.confirm(`Excluir avaliacao de ${row.nome}? Esta acao nao pode ser desfeita.`);
    if (!confirmed) return;

    setDeletingId(row.id);
    setError(null);
    try {
      await apiFetch(`/admin/lecture-feedback/${row.id}`, { method: "DELETE", auth: "admin" });
      await loadFeedback();
    } catch {
      setError("Falha ao excluir avaliacao.");
    } finally {
      setDeletingId(null);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const todayMetric = useMemo(() => data?.metrics[0] ?? null, [data]);

  return (
    <>
      <div className="adm-topbar">
        <div>
          <h1 className="adm-title">Palestras</h1>
          <p className="adm-sub">{data?.total ?? 0} avaliacoes registradas</p>
        </div>
      </div>

      {error && <div className="adm-login-error">{error}</div>}

      <div className="adm-cards palestra-admin-cards">
        <div className="adm-card">
          <div className="adm-card-label">Total</div>
          <div className="adm-card-value">{data?.total ?? 0}</div>
          <div className="adm-card-delta">formularios enviados</div>
        </div>
        <div className="adm-card">
          <div className="adm-card-label">Media recente</div>
          <div className="adm-card-value">{todayMetric ? todayMetric.avg_rating.toFixed(1) : "0.0"}</div>
          <div className="adm-card-delta">ultimo dia com respostas</div>
        </div>
        <div className="adm-card">
          <div className="adm-card-label">{todayMetric ? fmtMetricDay(todayMetric.date) : "Hoje"}</div>
          <div className="adm-card-value">{todayMetric ? SENTIMENT_LABEL[dayTone(todayMetric)] : "-"}</div>
          <div className={`adm-card-delta ${todayMetric && dayTone(todayMetric) === "ruim" ? "down" : ""}`}>
            {todayMetric ? `${todayMetric.boa} boas · ${todayMetric.ruim} ruins` : "sem dados"}
          </div>
        </div>
      </div>

      <div className="adm-panel palestra-metrics-panel">
        <div className="adm-panel-head">
          <h2 className="adm-panel-title">Metrica por dia</h2>
        </div>
        <div className="palestra-metric-grid">
          {data?.metrics.length === 0 && <span className="adm-sub">Nenhuma metrica ainda.</span>}
          {data?.metrics.map((metric) => {
            const tone = dayTone(metric);
            return (
              <div className={`palestra-metric ${tone}`} key={metric.date}>
                <span>{fmtMetricDay(metric.date)}</span>
                <strong>{SENTIMENT_LABEL[tone]}</strong>
                <small>
                  {metric.total} respostas · media {metric.avg_rating.toFixed(1)}
                </small>
              </div>
            );
          })}
        </div>
      </div>

      <div className="adm-panel">
        <div className="adm-filters">
          <input
            className="adm-input"
            placeholder="Buscar por nome, e-mail ou telefone..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ minWidth: 320 }}
          />
        </div>

        <table className="adm-table palestra-admin-table">
          <thead>
            <tr>
              <th>Data e hora</th>
              <th>Nome</th>
              <th>Contato</th>
              <th>Nota</th>
              <th>Avaliacao</th>
              <th>Comentario</th>
              <th style={{ width: 84 }}>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} style={{ color: "var(--ink-3)" }}>Carregando...</td>
              </tr>
            )}
            {!loading && data?.feedback.length === 0 && (
              <tr>
                <td colSpan={7} style={{ color: "var(--ink-3)" }}>Nenhuma avaliacao encontrada.</td>
              </tr>
            )}
            {!loading && data?.feedback.map((row) => (
              <tr key={row.id}>
                <td data-label="Data">{fmtDateTime(row.created_at)}</td>
                <td data-label="Nome" style={{ fontWeight: 600 }}>{row.nome}</td>
                <td data-label="Contato">
                  <span className="palestra-contact">{row.email}</span>
                  <span className="palestra-contact">{row.telefone}</span>
                </td>
                <td data-label="Nota">
                  <span className="palestra-rating-admin">{"★".repeat(row.rating)}</span>
                </td>
                <td data-label="Avaliacao">
                  <span className={`adm-pill p-${row.sentiment}`}>{SENTIMENT_LABEL[row.sentiment]}</span>
                </td>
                <td data-label="Comentario" className="palestra-comment">
                  {row.comentario || <span className="adm-answer-empty">Sem comentario</span>}
                </td>
                <td data-label="Acoes">
                  <button
                    type="button"
                    className="adm-btn-icon palestra-delete-btn"
                    title="Excluir avaliacao"
                    aria-label={`Excluir avaliacao de ${row.nome}`}
                    disabled={deletingId === row.id}
                    onClick={() => handleDelete(row)}
                  >
                    <IconTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {data && data.total > data.pageSize && (
          <div className="adm-pagination">
            <button
              type="button"
              className="btn btn-ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{ padding: "10px 18px", fontSize: 14 }}
            >
              ← Anterior
            </button>
            <span className="adm-pagination-label">
              Pagina {page} de {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              style={{ padding: "10px 18px", fontSize: 14 }}
            >
              Proxima →
            </button>
          </div>
        )}
      </div>
    </>
  );
}
